import React, { useState, useEffect, useCallback } from 'react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { PieChart, Plus, Edit2, Trash2, X, Save, Tag, BarChart3, RefreshCw, Webhook, CheckCircle, XCircle, Send, Eye, EyeOff } from 'lucide-react';

const CORES = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#6366f1',
];

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: `1px solid ${colors.border}`, background: colors.bg,
  color: colors.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 };

// =================== COMPONENTE PRINCIPAL ===================
export default function CentrosCustoPage() {
  const [tab, setTab] = useState('centros');
  const [centros, setCentros] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [relatorio, setRelatorio] = useState(null);
  const [mesFiltro, setMesFiltro] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);

  const fetchCentros = useCallback(async () => {
    const r = await fetch(`${API}/centros-custo`, { headers: authHeaders() });
    if (r.ok) setCentros(await r.json());
  }, []);

  const fetchCategorias = useCallback(async () => {
    const r = await fetch(`${API}/centros-custo/categorias`, { headers: authHeaders() });
    if (r.ok) setCategorias(await r.json());
  }, []);

  const fetchRelatorio = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/centros-custo/relatorio?mes=${mesFiltro}`, { headers: authHeaders() });
    if (r.ok) setRelatorio(await r.json());
    setLoading(false);
  }, [mesFiltro]);

  useEffect(() => { fetchCentros(); fetchCategorias(); }, [fetchCentros, fetchCategorias]);
  useEffect(() => { if (tab === 'relatorio') fetchRelatorio(); }, [tab, fetchRelatorio]);

  const tabs = [
    { id: 'centros', label: '🏢 Centros de Custo' },
    { id: 'categorias', label: '🏷️ Categorias & Templates' },
    { id: 'relatorio', label: '📊 Relatório' },
    { id: 'webhooks', label: '🔔 Webhooks' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 10 }}>
          <PieChart size={24} color={colors.primary} /> Centro de Custos
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: colors.textDim }}>
          Gerencie empresas, categorias e distribua gastos entre centros de custo
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${colors.border}`, paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: tab === t.id ? colors.primary : colors.textMuted,
            borderBottom: tab === t.id ? `2px solid ${colors.primary}` : '2px solid transparent',
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'centros' && (
        <CentrosTab centros={centros} onRefresh={fetchCentros} />
      )}
      {tab === 'categorias' && (
        <CategoriasTab categorias={categorias} centros={centros} onRefresh={fetchCategorias} />
      )}
      {tab === 'relatorio' && (
        <RelatorioTab relatorio={relatorio} loading={loading} mesFiltro={mesFiltro}
          setMesFiltro={setMesFiltro} onRefresh={fetchRelatorio} />
      )}
      {tab === 'webhooks' && (
        <WebhooksTab centros={centros} />
      )}
    </div>
  );
}

// =================== ABA: CENTROS ===================
function CentrosTab({ centros, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nome: '', descricao: '', cor: '#3b82f6', ordem: 0 });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const openNew = () => { setForm({ nome: '', descricao: '', cor: '#3b82f6', ordem: centros.length }); setEditId(null); setErro(''); setShowModal(true); };
  const openEdit = (c) => { setForm({ nome: c.nome, descricao: c.descricao || '', cor: c.cor || '#3b82f6', ordem: c.ordem || 0 }); setEditId(c.id); setErro(''); setShowModal(true); };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome obrigatorio'); return; }
    setSaving(true);
    const url = editId ? `${API}/centros-custo/${editId}` : `${API}/centros-custo`;
    const r = await fetch(url, { method: editId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const data = await r.json();
    setSaving(false);
    if (!r.ok) { setErro(data.error || 'Erro ao salvar'); return; }
    setShowModal(false);
    onRefresh();
  };

  const desativar = async (id) => {
    if (!window.confirm('Desativar este centro de custo?')) return;
    await fetch(`${API}/centros-custo/${id}`, { method: 'DELETE', headers: authHeaders() });
    onRefresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={openNew} style={{
          padding: '10px 20px', borderRadius: 8, border: 'none',
          background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}><Plus size={16} /> Novo Centro</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {centros.map(c => (
          <div key={c.id} style={{
            background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12,
            padding: 18, borderLeft: `4px solid ${c.cor}`,
            opacity: c.ativo ? 1 : 0.5,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>{c.nome}</div>
                {c.descricao && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{c.descricao}</div>}
                <div style={{ marginTop: 8, width: 14, height: 14, borderRadius: '50%', background: c.cor, display: 'inline-block' }} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => openEdit(c)} style={{ background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 6px', color: colors.primary, cursor: 'pointer' }}><Edit2 size={13} /></button>
                <button onClick={() => desativar(c.id)} style={{ background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 6px', color: colors.danger, cursor: 'pointer' }}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowModal(false)}>
          <div style={{ width: 440, background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text }}>{editId ? 'Editar Centro' : 'Novo Centro de Custo'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Nome *</label>
                <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} placeholder="Ex: XMiles, iVoei..." />
              </div>
              <div>
                <label style={labelStyle}>Descrição</label>
                <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} style={inputStyle} placeholder="Opcional" />
              </div>
              <div>
                <label style={labelStyle}>Cor de identificação</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {CORES.map(cor => (
                    <button key={cor} onClick={() => setForm({ ...form, cor })}
                      style={{ width: 28, height: 28, borderRadius: '50%', background: cor, border: form.cor === cor ? `3px solid ${colors.text}` : '2px solid transparent', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
              {erro && <div style={{ color: colors.danger, fontSize: 12 }}>{erro}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                <button onClick={salvar} disabled={saving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== ABA: CATEGORIAS & TEMPLATES ===================
function CategoriasTab({ categorias, centros, onRefresh }) {
  const [showCatModal, setShowCatModal] = useState(false);
  const [catForm, setCatForm] = useState({ nome: '', descricao: '' });
  const [editCatId, setEditCatId] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null); // null = padrão global
  const [template, setTemplate] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [erro, setErro] = useState('');

  const loadTemplate = useCallback(async (catId) => {
    setTemplateLoading(true);
    const param = catId === null ? 'null' : catId;
    const r = await fetch(`${API}/centros-custo/templates?categoria_id=${param}`, { headers: authHeaders() });
    const existentes = r.ok ? await r.json() : [];
    // Montar rateio para todos os centros ativos
    const rateio = centros.filter(c => c.ativo).map(c => {
      const found = existentes.find(e => e.centro_custo_id === c.id);
      return { centro_custo_id: c.id, nome: c.nome, cor: c.cor, percentual: found ? parseFloat(found.percentual) : 0 };
    });
    setTemplate(rateio);
    setTemplateLoading(false);
  }, [centros]);

  useEffect(() => { if (centros.length) loadTemplate(selectedCat); }, [selectedCat, centros, loadTemplate]);

  const updatePct = (centroId, val) => {
    const num = parseFloat(val) || 0;
    setTemplate(prev => prev.map(r => r.centro_custo_id === centroId ? { ...r, percentual: num } : r));
  };

  const totalPct = template.reduce((s, r) => s + (r.percentual || 0), 0);

  const salvarTemplate = async () => {
    if (Math.abs(totalPct - 100) > 0.1) { setErro(`Percentuais devem somar 100% (atual: ${totalPct.toFixed(1)}%)`); return; }
    setErro('');
    setTemplateSaving(true);
    const r = await fetch(`${API}/centros-custo/templates`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ categoria_id: selectedCat, rateios: template }),
    });
    const data = await r.json();
    setTemplateSaving(false);
    if (!r.ok) { setErro(data.error || 'Erro ao salvar'); return; }
    setErro('');
    alert('Template salvo!');
  };

  const salvarCategoria = async () => {
    if (!catForm.nome.trim()) return;
    const url = editCatId ? `${API}/centros-custo/categorias/${editCatId}` : `${API}/centros-custo/categorias`;
    const r = await fetch(url, { method: editCatId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(catForm) });
    if (r.ok) { setShowCatModal(false); onRefresh(); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24 }}>
      {/* Lista de categorias */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.textMuted }}>CATEGORIAS</span>
          <button onClick={() => { setCatForm({ nome: '', descricao: '' }); setEditCatId(null); setShowCatModal(true); }} style={{
            padding: '5px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
            background: 'transparent', color: colors.primary, fontSize: 12, cursor: 'pointer',
          }}><Plus size={12} style={{ marginRight: 4 }} />Nova</button>
        </div>

        {/* Opção: template padrão global */}
        <div onClick={() => setSelectedCat(null)}
          style={{
            padding: '10px 14px', borderRadius: 8, cursor: 'pointer', marginBottom: 6,
            background: selectedCat === null ? `${colors.primary}20` : colors.card,
            border: `1px solid ${selectedCat === null ? colors.primary : colors.border}`,
          }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: selectedCat === null ? colors.primary : colors.text }}>⭐ Padrão Global</div>
          <div style={{ fontSize: 11, color: colors.textDim }}>Usado quando nenhuma categoria é selecionada</div>
        </div>

        {categorias.map(c => (
          <div key={c.id} onClick={() => setSelectedCat(c.id)}
            style={{
              padding: '10px 14px', borderRadius: 8, cursor: 'pointer', marginBottom: 6,
              background: selectedCat === c.id ? `${colors.primary}20` : colors.card,
              border: `1px solid ${selectedCat === c.id ? colors.primary : colors.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: selectedCat === c.id ? colors.primary : colors.text }}>{c.nome}</div>
              {c.descricao && <div style={{ fontSize: 11, color: colors.textDim }}>{c.descricao}</div>}
            </div>
            <button onClick={e => { e.stopPropagation(); setCatForm({ nome: c.nome, descricao: c.descricao || '' }); setEditCatId(c.id); setShowCatModal(true); }}
              style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer', padding: 4 }}><Edit2 size={12} /></button>
          </div>
        ))}
      </div>

      {/* Editor de template */}
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`, padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.text }}>
            Template: {selectedCat === null ? '⭐ Padrão Global' : categorias.find(c => c.id === selectedCat)?.nome}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textDim }}>
            Define como os gastos desta categoria são distribuídos entre as empresas
          </p>
        </div>

        {templateLoading ? (
          <div style={{ color: colors.textDim, fontSize: 13 }}>Carregando...</div>
        ) : (
          <>
            {template.map(r => (
              <div key={r.centro_custo_id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: r.cor, flexShrink: 0 }} />
                <div style={{ width: 160, fontSize: 13, fontWeight: 600, color: colors.text }}>{r.nome}</div>
                <input
                  type="number" min={0} max={100} step={0.1}
                  value={r.percentual === 0 ? '' : r.percentual}
                  onChange={e => updatePct(r.centro_custo_id, e.target.value)}
                  placeholder="0"
                  style={{ ...inputStyle, width: 90, textAlign: 'right' }}
                />
                <span style={{ fontSize: 13, color: colors.textMuted }}>%</span>
                {/* Barra visual */}
                <div style={{ flex: 1, height: 6, background: colors.bg, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(r.percentual, 100)}%`, height: '100%', background: r.cor, borderRadius: 3, transition: 'width 0.2s' }} />
                </div>
              </div>
            ))}

            {/* Total */}
            <div style={{
              marginTop: 16, padding: '10px 14px', borderRadius: 8,
              background: Math.abs(totalPct - 100) <= 0.1 ? `${colors.success}15` : `${colors.warning}15`,
              border: `1px solid ${Math.abs(totalPct - 100) <= 0.1 ? colors.success : colors.warning}30`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: Math.abs(totalPct - 100) <= 0.1 ? colors.success : colors.warning }}>
                Total: {totalPct.toFixed(1)}%
                {Math.abs(totalPct - 100) <= 0.1 ? ' ✓' : ` (falta ${(100 - totalPct).toFixed(1)}%)`}
              </span>
              <button onClick={salvarTemplate} disabled={templateSaving || Math.abs(totalPct - 100) > 0.1} style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: Math.abs(totalPct - 100) <= 0.1 ? colors.primary : colors.textDim,
                color: '#fff', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Save size={14} />{templateSaving ? 'Salvando...' : 'Salvar Template'}
              </button>
            </div>
            {erro && <div style={{ marginTop: 8, color: colors.danger, fontSize: 12 }}>{erro}</div>}
          </>
        )}
      </div>

      {/* Modal categoria */}
      {showCatModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowCatModal(false)}>
          <div style={{ width: 400, background: colors.card, borderRadius: 14, border: `1px solid ${colors.border}`, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text }}>{editCatId ? 'Editar Categoria' : 'Nova Categoria'}</h3>
              <button onClick={() => setShowCatModal(false)} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={labelStyle}>Nome *</label>
                <input value={catForm.nome} onChange={e => setCatForm({ ...catForm, nome: e.target.value })} style={inputStyle} placeholder="Ex: Aluguel, Marketing, TI..." />
              </div>
              <div>
                <label style={labelStyle}>Descrição</label>
                <input value={catForm.descricao} onChange={e => setCatForm({ ...catForm, descricao: e.target.value })} style={inputStyle} placeholder="Opcional" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button onClick={() => setShowCatModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                <button onClick={salvarCategoria} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== ABA: WEBHOOKS ===================
function WebhooksTab({ centros }) {
  const [webhooks, setWebhooks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [logsModal, setLogsModal] = useState(null); // webhook selecionado para ver logs
  const [logs, setLogs] = useState([]);
  const [testando, setTestando] = useState(null);
  const [testeResultado, setTesteResultado] = useState({});

  const fetchWebhooks = useCallback(async () => {
    const r = await fetch(`${API}/centros-custo/webhooks`, { headers: authHeaders() });
    if (r.ok) setWebhooks(await r.json());
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const abrirLogs = async (wh) => {
    setLogsModal(wh);
    const r = await fetch(`${API}/centros-custo/webhooks/${wh.id}/logs`, { headers: authHeaders() });
    if (r.ok) setLogs(await r.json());
  };

  const testar = async (wh) => {
    setTestando(wh.id);
    setTesteResultado(prev => ({ ...prev, [wh.id]: null }));
    try {
      const r = await fetch(`${API}/centros-custo/webhooks/${wh.id}/testar`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await r.json();
      setTesteResultado(prev => ({ ...prev, [wh.id]: data }));
    } catch (e) {
      setTesteResultado(prev => ({ ...prev, [wh.id]: { erro: e.message } }));
    }
    setTestando(null);
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este webhook?')) return;
    await fetch(`${API}/centros-custo/webhooks/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchWebhooks();
  };

  const toggleAtivo = async (wh) => {
    await fetch(`${API}/centros-custo/webhooks/${wh.id}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ ativo: !wh.ativo }),
    });
    fetchWebhooks();
  };

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.text }}>Integrações via Webhook</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textDim }}>
            Quando um lançamento tiver rateio com centros configurados (iVoei, iVoei Franchising…),
            o XM Controle avisa automaticamente o XM Agencia com todos os dados financeiros.
          </p>
        </div>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{
          padding: '10px 20px', borderRadius: 8, border: 'none',
          background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        }}><Plus size={16} /> Novo Webhook</button>
      </div>

      {/* Explicação do fluxo */}
      <div style={{
        padding: '14px 18px', borderRadius: 10, marginBottom: 20,
        background: `${colors.info}10`, border: `1px solid ${colors.info}30`,
        fontSize: 13, color: colors.text,
      }}>
        <div style={{ fontWeight: 700, color: colors.info, marginBottom: 6 }}>🔄 Como funciona</div>
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, color: colors.textMuted }}>
          <li>Lançamento é feito no XM Controle e recebe rateio por centro de custo</li>
          <li>Se o rateio incluir <b style={{ color: colors.text }}>iVoei</b> ou <b style={{ color: colors.text }}>iVoei Franchising</b>, dispara webhook automaticamente</li>
          <li>XM Agencia recebe o POST com todos os dados + percentual + valor por empresa</li>
          <li>XM Agencia registra o lançamento para validação interna</li>
        </ol>
        <div style={{ marginTop: 8, fontSize: 11, color: colors.textDim }}>
          O payload inclui: dados do lançamento, cartão, titular, data, valor, categoria e o rateio completo por centro de custo.
          Segurança: cada webhook pode ter um <code>secret</code> — o XM Agencia valida o header <code>X-XM-Signature</code>.
        </div>
      </div>

      {/* Lista de webhooks */}
      {webhooks.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.textDim, fontSize: 13 }}>
          Nenhum webhook configurado ainda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {webhooks.map(wh => {
            const triggers = JSON.parse(wh.centros_trigger || '[]');
            const res = testeResultado[wh.id];
            return (
              <div key={wh.id} style={{
                background: colors.card, border: `1px solid ${wh.ativo ? colors.border : colors.border + '44'}`,
                borderRadius: 12, padding: '16px 20px',
                borderLeft: `4px solid ${wh.ativo ? colors.success : colors.textDim}`,
                opacity: wh.ativo ? 1 : 0.65,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{wh.nome}</span>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                        background: wh.ativo ? `${colors.success}20` : `${colors.textDim}20`,
                        color: wh.ativo ? colors.success : colors.textDim,
                      }}>{wh.ativo ? 'ATIVO' : 'INATIVO'}</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textDim, fontFamily: 'monospace', marginBottom: 6,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wh.url}</div>
                    {wh.descricao && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 6 }}>{wh.descricao}</div>}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {triggers.length === 0 ? (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: `${colors.warning}15`, color: colors.warning }}>
                          Todos os centros
                        </span>
                      ) : triggers.map(nome => {
                        const centro = centros.find(c => c.nome === nome);
                        return (
                          <span key={nome} style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
                            background: centro ? `${centro.cor}20` : `${colors.primary}15`,
                            color: centro ? centro.cor : colors.primary,
                          }}>{nome}</span>
                        );
                      })}
                    </div>
                    {res && (
                      <div style={{
                        marginTop: 8, fontSize: 12, padding: '6px 10px', borderRadius: 6,
                        background: res.sucesso ? `${colors.success}15` : `${colors.danger}15`,
                        color: res.sucesso ? colors.success : colors.danger,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {res.sucesso ? <CheckCircle size={13} /> : <XCircle size={13} />}
                        {res.sucesso ? `Sucesso! HTTP ${res.statusCode}` : `Erro: ${res.erro || `HTTP ${res.statusCode}`}`}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => testar(wh)} disabled={testando === wh.id} title="Enviar ping de teste" style={{
                      padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: colors.info, cursor: 'pointer', fontSize: 12,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <Send size={13} />{testando === wh.id ? '...' : 'Testar'}
                    </button>
                    <button onClick={() => abrirLogs(wh)} title="Ver logs" style={{
                      padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: 12,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <Eye size={13} />Logs
                    </button>
                    <button onClick={() => toggleAtivo(wh)} title={wh.ativo ? 'Desativar' : 'Ativar'} style={{
                      padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: wh.ativo ? colors.warning : colors.success, cursor: 'pointer', fontSize: 12,
                    }}>
                      {wh.ativo ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                    <button onClick={() => { setEditItem(wh); setShowModal(true); }} style={{
                      padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: colors.primary, cursor: 'pointer',
                    }}><Edit2 size={13} /></button>
                    <button onClick={() => excluir(wh.id)} style={{
                      padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: colors.danger, cursor: 'pointer',
                    }}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal criar/editar */}
      {showModal && (
        <WebhookModal
          item={editItem}
          centros={centros}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchWebhooks(); }}
        />
      )}

      {/* Modal de logs */}
      {logsModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setLogsModal(null)}>
          <div style={{ width: 700, maxHeight: '80vh', background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text }}>Logs: {logsModal.nome}</h3>
              <button onClick={() => setLogsModal(null)} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={18} /></button>
            </div>
            {logs.length === 0 ? (
              <div style={{ color: colors.textDim, fontSize: 13, textAlign: 'center', padding: 30 }}>Nenhum disparo registrado ainda</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: `${colors.primary}10` }}>
                    {['Data/Hora', 'Lançamento', 'HTTP', 'Tentativas', 'Status'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 11, textTransform: 'uppercase', borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                      <td style={{ padding: '8px 12px', color: colors.textDim }}>{new Date(l.created_at).toLocaleString('pt-BR')}</td>
                      <td style={{ padding: '8px 12px', color: colors.text }}>
                        {l.transacao_descricao ? (
                          <div>
                            <div style={{ fontWeight: 600 }}>{l.transacao_descricao}</div>
                            <div style={{ color: colors.textDim }}>{l.transacao_valor ? `R$ ${parseFloat(l.transacao_valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}</div>
                          </div>
                        ) : <span style={{ color: colors.textDim }}>ping</span>}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ fontFamily: 'monospace', color: l.status_code >= 200 && l.status_code < 300 ? colors.success : colors.danger }}>
                          {l.status_code || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: colors.textMuted, textAlign: 'center' }}>{l.tentativas}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
                          background: l.sucesso ? `${colors.success}20` : `${colors.danger}15`,
                          color: l.sucesso ? colors.success : colors.danger,
                        }}>
                          {l.sucesso ? '✓ OK' : l.erro ? `✗ ${l.erro.slice(0, 30)}` : '✗ ERRO'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookModal({ item, centros, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome: item?.nome || '',
    url: item?.url || '',
    secret: '',
    descricao: item?.descricao || '',
    ativo: item?.ativo !== false,
    centros_trigger: item ? JSON.parse(item.centros_trigger || '[]') : ['iVoei', 'iVoei Franchising'],
  });
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const toggleCentro = (nome) => {
    setForm(prev => ({
      ...prev,
      centros_trigger: prev.centros_trigger.includes(nome)
        ? prev.centros_trigger.filter(n => n !== nome)
        : [...prev.centros_trigger, nome],
    }));
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome obrigatorio'); return; }
    if (!form.url.trim())  { setErro('URL obrigatoria'); return; }
    try { new URL(form.url); } catch { setErro('URL inválida'); return; }
    setErro('');
    setSaving(true);
    const body = { ...form };
    // Se secret estiver vazio e editando, não enviar (não alterar)
    if (!body.secret) delete body.secret;
    const url = item ? `${API}/centros-custo/webhooks/${item.id}` : `${API}/centros-custo/webhooks`;
    const r = await fetch(url, { method: item ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(body) });
    const data = await r.json();
    setSaving(false);
    if (!r.ok) { setErro(data.error || 'Erro ao salvar'); return; }
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ width: 560, background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text }}>{item ? 'Editar Webhook' : 'Novo Webhook'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Nome *</label>
            <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
              style={inputStyle} placeholder="Ex: XM Agencia — Notificações" />
          </div>
          <div>
            <label style={labelStyle}>URL do endpoint (POST) *</label>
            <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
              style={inputStyle} placeholder="https://xmagencia.exemplo.com/api/webhooks/custo" />
          </div>
          <div>
            <label style={labelStyle}>Secret (HMAC-SHA256) — opcional</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showSecret ? 'text' : 'password'}
                value={form.secret}
                onChange={e => setForm({ ...form, secret: e.target.value })}
                style={{ ...inputStyle, paddingRight: 40 }}
                placeholder={item?.secret ? 'Deixe em branco para manter o atual' : 'Chave secreta compartilhada'}
              />
              <button type="button" onClick={() => setShowSecret(!showSecret)} style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer', padding: 2,
              }}>
                {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 3 }}>
              O XM Agencia valida o header <code>X-XM-Signature: sha256=…</code> usando este secret.
            </div>
          </div>
          <div>
            <label style={labelStyle}>Descrição</label>
            <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })}
              style={inputStyle} placeholder="Opcional" />
          </div>

          {/* Centros que disparam este webhook */}
          <div>
            <label style={labelStyle}>Disparar quando o rateio incluir *</label>
            <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 8 }}>
              Selecione os centros de custo que ativam este webhook. Sem seleção = dispara sempre.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {centros.filter(c => c.ativo).map(c => {
                const sel = form.centros_trigger.includes(c.nome);
                return (
                  <button key={c.id} type="button" onClick={() => toggleCentro(c.nome)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      border: `2px solid ${sel ? c.cor : colors.border}`,
                      background: sel ? `${c.cor}20` : 'transparent',
                      color: sel ? c.cor : colors.textMuted,
                      transition: 'all 0.15s',
                    }}>
                    {sel ? '✓ ' : ''}{c.nome}
                  </button>
                );
              })}
            </div>
          </div>

          {erro && <div style={{ color: colors.danger, fontSize: 12 }}>{erro}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
            <button onClick={salvar} disabled={saving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={14} />{saving ? 'Salvando...' : 'Salvar Webhook'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== ABA: RELATÓRIO ===================
function RelatorioTab({ relatorio, loading, mesFiltro, setMesFiltro, onRefresh }) {
  const total = relatorio?.por_centro?.reduce((s, c) => s + parseFloat(c.total_calculado || 0), 0) || 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Mês de referência</label>
          <input type="month" value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}
            style={{ ...inputStyle, width: 180 }} />
        </div>
        <button onClick={onRefresh} style={{
          marginTop: 18, padding: '9px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
          background: colors.card, color: colors.primary, fontSize: 13, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}><RefreshCw size={14} />Atualizar</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: colors.textDim }}>Carregando...</div>
      ) : !relatorio ? null : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
            {relatorio.por_centro.map(c => {
              const valor = parseFloat(c.total_calculado || 0);
              const pct = total > 0 ? (valor / total * 100) : 0;
              return (
                <div key={c.id} style={{
                  background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12,
                  padding: 18, borderLeft: `4px solid ${c.cor}`,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginBottom: 6 }}>{c.nome}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: c.cor }}>{fmt(valor)}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>{pct.toFixed(1)}% do total · {c.qtd_transacoes} transações</div>
                  <div style={{ marginTop: 10, height: 6, background: colors.bg, borderRadius: 3 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: c.cor, borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {parseFloat(relatorio.sem_rateio?.total || 0) > 0 && (
            <div style={{
              padding: '12px 16px', borderRadius: 10,
              background: `${colors.warning}10`, border: `1px solid ${colors.warning}30`,
              color: colors.warning, fontSize: 13,
            }}>
              ⚠️ <b>{relatorio.sem_rateio.total}</b> transações sem rateio — {fmt(relatorio.sem_rateio.valor)} não classificados
            </div>
          )}

          <div style={{ marginTop: 20, background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: `${colors.primary}10` }}>
                  {['Centro de Custo', 'Transações', 'Valor Total', '% do Total'].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 11, textTransform: 'uppercase', borderBottom: `1px solid ${colors.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {relatorio.por_centro.map(c => {
                  const valor = parseFloat(c.total_calculado || 0);
                  const pct = total > 0 ? (valor / total * 100) : 0;
                  return (
                    <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.cor }} />
                          <span style={{ fontWeight: 600, color: colors.text }}>{c.nome}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px', color: colors.textMuted }}>{c.qtd_transacoes}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: c.cor }}>{fmt(valor)}</td>
                      <td style={{ padding: '10px 14px', color: colors.textMuted }}>{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                <tr style={{ background: `${colors.primary}08` }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: colors.text }}>TOTAL</td>
                  <td />
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: colors.primary }}>{fmt(total)}</td>
                  <td style={{ padding: '10px 14px', color: colors.textMuted }}>100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
